/**
 * menus.ts — 메뉴·권한 단일 소스 (Single Source of Truth).
 *
 * 사이드바(nav) · 권한 카탈로그(그룹 편집기) · 라우트 게이트가 모두 여기서 파생된다.
 * 이전에는 nav.ts / permissions.ts / routes.tsx 가 각자 목록을 들고 손으로 동기화했음.
 *
 * 권한 두 갈래 (그룹 편집기 UI 구분):
 *   - 메뉴권한(gate): 이 메뉴가 사이드바에 보이고 진입 가능한지. 전부 `*.view` 키.
 *                     gate=null 이면 로그인만으로 항상 표시(대시보드/환경설정 등).
 *   - 업무권한(work): 그 메뉴 안에서 할 수 있는 기능(create/edit/delete/import…).
 *
 * ⚠️ 새 메뉴·권한 키를 코드/rules에 추가하면 여기 한 곳만 갱신하면 된다.
 * 관리자(admin) 그룹은 is_admin 클레임으로 모든 권한을 우회하므로 이 목록과 무관.
 */
import type { IconName } from "@/components/ui/Icon";
import {
  BIZ_KEYS,
  LEGACY_TENANT,
  TENANT_BIZ,
  bizHomePath,
  defaultBiz,
  isTenantKey,
  parseSpacePath,
  tenantHasBiz,
  tenantRootPath,
  type BizKey,
  type SpaceKey,
  type TenantKey,
} from "@/domain/space";

export interface WorkPerm {
  key: string;
  label: string;
}

export interface MenuDef {
  /** 라우트/사이드바 키 (= URL path) */
  key: string;
  label: string;
  icon: IconName;
  /** 사이드바 그룹 헤더. ""(빈 문자열)이면 그룹 없이 최상단. */
  group: string;
  /**
   * 메뉴권한 — 이 메뉴를 보려면 필요한 권한(anyOf). 전부 `*.view` 키.
   * null 이면 로그인만으로 항상 표시(토글 불가).
   */
  gate: string[] | null;
  /** 업무권한 — 메뉴 안의 기능 권한 */
  work: WorkPerm[];
  /** 사이드바 라벨 옆 작은 표식 (예: alias 안내, live) */
  marker?: string;
  /** 페이지 미완성(ComingSoon) — 사이드바엔 표시하되 진입 시 준비중 */
  comingSoon?: boolean;
  /** 그룹 편집기 메뉴권한 목록에서 숨김 (디자인 비교용 샘플 등) */
  hideInRoleEditor?: boolean;
  /**
   * 권한 편집기 묶음(공통·영업·생산·출하·자재·재무·시스템). 사이드바 group 과 별개 —
   * 사이드바 그룹·URL·메뉴 키는 그대로 두고 편집기 표시만 기능 단위로 묶는다(wiki/시스템/부서별-권한.md §3).
   * 생략 = "공통".
   */
  permGroup?: PermGroup;
  /**
   * 이 메뉴가 보이는 공간(B′ 미리보기, domain/space.ts). 생략 = 중앙산업만(현행 메뉴).
   * 권한 판정(gate)과는 별개 — 공간은 "어느 사이드바에 놓이나", gate 는 "볼 수 있나".
   */
  spaces?: SpaceKey[];
  /**
   * 법인 › 사업 2단계(D-26) 중 어느 단위 메뉴인가. 생략 = "biz"(사업별: `/c/{tenant}/{biz}/{key}`).
   * "tenant" = 법인 공통(급여·원가·환경설정: `/c/{tenant}/{key}`). 내 업무 메뉴에는 쓰지 않는다.
   */
  scope?: "tenant" | "biz";
  /**
   * 이 메뉴가 놓이는 사업(2026-10-29). 생략 = 지금 메뉴 목록이 만들어진 사업(LEGACY_BIZ: 레미콘·아스콘).
   * 석산(aggregate)처럼 새로 여는 사업은 여기 명시한 메뉴만 사이드바·주소에 붙는다 — 레미콘 메뉴가 복사돼 보이지 않게.
   * 법인 공통(scope "tenant") 메뉴도 같은 규칙(환경설정만 전 사업).
   */
  biz?: readonly BizKey[];
}

/** 권한 편집기 묶음 — 이 순서대로 표시 (부서별 권한 설계 §3). 석산은 사업 전용 메뉴 묶음(2026-10-29) */
export const PERM_GROUPS = ["공통", "영업", "생산·출하", "자재", "재무", "시스템", "석산"] as const;
export type PermGroup = (typeof PERM_GROUPS)[number];

/** 중앙·경원 양쪽 법인 공간에 다 있는 메뉴 */
const BOTH: SpaceKey[] = ["joongang", "gyeongwon"];
/** 지금 메뉴 목록이 만들어진 사업 — `biz` 생략 메뉴의 기본값(레미콘·아스콘). 석산은 명시한 메뉴만. */
export const LEGACY_BIZ: readonly BizKey[] = ["rmc", "ascon"];

export const MENU_REGISTRY: MenuDef[] = [
  {
    // 내부 key 는 "dashboard" 유지(권한키 stats.view 등 연쇄 변경 회피).
    // 사용자 노출 라벨만 "홈", URL 은 루트 "/" 가 곧 홈 (routes.tsx 참고).
    key: "dashboard", label: "홈", icon: "home", group: "",
    spaces: BOTH, permGroup: "공통",
    gate: null,
    work: [{ key: "stats.view", label: "통계 조회" }],
  },

  // ── 영업 ──
  {
    key: "quote", label: "현장견적", icon: "quote", group: "영업",
    spaces: BOTH, permGroup: "영업",
    gate: ["quote.view"],
    work: [
      { key: "quote.edit", label: "견적 작성·수정" },
      { key: "quote.settings", label: "견적 기본값 설정" },
      // 원가 원본(배합 투입량·자재 단가·기타비용 항목)을 직접 읽는 권한. 없으면 서버(quoteCostCalc)가
      // 환경설정 "원가 상세 공개"(cost_visibility) 범위만 계산해 보낸다 — 화면 가림이 아니라 데이터 차단.
      { key: "quote.cost_detail", label: "원가 상세 열람" },
      // 다른 직원이 저장한 견적까지 조회. 없으면 본인 견적만. 남의 견적 수정·삭제는 admin 만.
      { key: "quote.view_all", label: "전체 견적 조회" },
      // 재무 모드(자차 운반비에 기사 급여 반영) 사용. 없으면 표준 모드로만 계산·표시된다.
      { key: "quote.finance_mode", label: "재무 모드 사용" },
    ],
  },
  {
    key: "market", label: "단가표", icon: "market", group: "영업", permGroup: "영업",
    gate: ["prices.view"],
    work: [{ key: "prices.edit", label: "단가표 편집" }],
  },
  {
    // 옛 "경로계산"(route) 개편 — 일자별 출하 예정으로 필요 대수·용차·종료시간·회전수 산출.
    key: "ship-sim", label: "출하 시뮬레이션", icon: "route", group: "영업", permGroup: "영업",
    gate: ["ship_sim.view"],
    work: [{ key: "ship_sim.edit", label: "현장 위치·보정 · 기본값 저장" }],
  },

  // ── 영업관리 ──
  {
    key: "sales", label: "판매관리", icon: "coin", group: "영업관리",
    spaces: BOTH, permGroup: "영업",
    gate: ["sales.view"],
    work: [
      // 거래처 부가정보(sales_customer_meta: 등급·태그·연락처·메모)와 코멘트 편집. 보기는 sales.view.
      { key: "sales.customer_edit", label: "거래처 메모·등급 편집" },
    ],
  },

  // ── 생산관리 ──
  {
    key: "dispatch-view", label: "출하조회", icon: "truck", group: "생산관리",
    spaces: BOTH, permGroup: "생산·출하",
    // dispatch.view 는 shipment.view 의 옛 별칭(PERM_LEGACY_ALIASES) — 판정엔 남기고 편집기 토글로는 안 보인다
    gate: ["shipment.view", "dispatch.view"], marker: "live",
    work: [
      { key: "dispatch.edit", label: "출하 설정 편집" },
      // 4자리 배합 코드(bcode·code) 표시. 규격 문자열(25-24-150)은 shipment.view 만으로 보인다(§6-3).
      { key: "shipment.mix", label: "배합 코드 보기" },
    ],
  },
  {
    // 출하실적 — 지나간 기간의 집계·비교(월간 캘린더 / 차량별 회전수 / 연도 추세).
    // 출하조회(실시간 오늘)와 성격이 달라 별도 메뉴. 권한은 출하 게이트를 그대로 쓴다.
    key: "shipment-stats", label: "출하실적", icon: "receipt", group: "생산관리",
    spaces: BOTH, permGroup: "생산·출하",
    gate: ["shipment.view", "dispatch.view"],
    work: [],
  },
  {
    // 차량·운반비 — 경원산업 전용 자리(B′ 미리보기). 화면은 아직 없음(comingSoon).
    // 새 권한 키를 만들지 않고 출하 게이트를 그대로 쓴다(권한 편집기·rules 영향 없음).
    key: "vehicle-cost", label: "차량·운반비", icon: "route", group: "생산관리",
    spaces: ["gyeongwon"],
    gate: ["shipment.view", "dispatch.view"],
    work: [], comingSoon: true, marker: "준비중", hideInRoleEditor: true,
  },
  {
    // 자재 소요 예측 — 출하 예정 × 배합표로 그날 필요 원자재 집계.
    // 배합 투입량·입출고(입고 단가 포함)를 읽어 출하조회와 권한을 분리했다(2026-10-12).
    // 출하 예정 데이터도 쓰므로 실사용자에게는 출하조회 권한도 함께 준다.
    key: "material-forecast", label: "자재 소요 예측", icon: "cube", group: "생산관리", permGroup: "생산·출하",
    gate: ["forecast.view"],
    work: [],
  },
  {
    key: "raw-materials", label: "원자재 관리", icon: "ledger", group: "생산관리", permGroup: "자재",
    gate: ["ledger.view"],
    work: [
      // 입고 단가·운반비·공급사 단가·당월 금액·원자재 단가 탭. material.view 는 옛 별칭(PERM_LEGACY_ALIASES).
      // 지금은 화면 표시만 가린다 — 실제 데이터 차단은 단가 분리(§5-C) 때 문서를 떼어 rules 로 막는다.
      { key: "material.price", label: "원자재 단가·금액 보기" },
      { key: "ledger.create", label: "입출고 등록" },
      { key: "ledger.edit", label: "입출고 수정" },
      { key: "ledger.delete", label: "입출고 삭제" },
      { key: "ledger.admin", label: "입출고 관리자" },
      { key: "material.create", label: "자재 추가" },
      { key: "material.edit", label: "자재 편집" },
      { key: "material.delete", label: "자재 삭제" },
    ],
  },
  // ── 재무 ──
  {
    key: "cost", label: "원가 관리", icon: "cost", group: "재무",
    spaces: BOTH, permGroup: "재무", scope: "tenant",
    gate: ["cost.view"],
    work: [{ key: "cost.edit", label: "원가 편집" }],
  },
  {
    // 급여관리 — 세무사 zip 업로드 → 브라우저 파싱·검산 → 명세서 조회. 명부 마스터 포함.
    // payroll.import = 명부 엑셀 업로드 + 급여 zip 적재(민감 데이터 write 게이트).
    key: "payroll", label: "급여관리", icon: "card", group: "재무",
    spaces: BOTH, permGroup: "재무", scope: "tenant",
    gate: ["payroll.view"],
    work: [{ key: "payroll.import", label: "명부·급여 등록" }],
  },

  // ── 기준정보 ──
  {
    key: "mix", label: "배합비 관리", icon: "mix", group: "기준정보",
    spaces: BOTH, permGroup: "자재",
    gate: ["mix.view"],
    work: [
      { key: "mix.create", label: "배합비 추가" },
      { key: "mix.edit", label: "배합비 편집" },
      { key: "mix.delete", label: "배합비 삭제" },
      { key: "mix.import", label: "배합비 가져오기" },
      { key: "mix.export", label: "배합비 내보내기" },
    ],
  },
  // ── 석산(aggregate, 중앙산업 두 번째 사업) — 2026-10-29. 사업부지(등기부 지도) 하나만 운영.
  //    레미콘 메뉴는 이 사업에 붙이지 않는다(biz 생략 = 레미콘·아스콘). 법인 공통은 환경설정만(위 biz: BIZ_KEYS).
  //    화면은 subprojects/land-registry 정적 앱을 iframe 으로(web/public/land-registry). 홈(대시보드) 없음 → 사업 홈 = 이 메뉴.
  {
    key: "land-registry", label: "사업부지", icon: "pin", group: "",
    spaces: ["joongang"], biz: ["aggregate"], permGroup: "석산",
    gate: ["aggregate.view"],
    work: [],
  },

  // ── 시스템 ──
  {
    key: "settings", label: "환경설정", icon: "settings", group: "시스템",
    spaces: BOTH, permGroup: "시스템", scope: "tenant", biz: BIZ_KEYS,
    gate: ["settings.view"],
    work: [
      // 법인 관리자 지위(isTenantAdmin)와 같은 뜻 — 그 법인 소속자에게 부서(역할)를 줄 수 있음(§6-1)
      { key: "members.manage", label: "권한 부여(법인)" },
      { key: "admin.audit.view", label: "감사 로그 조회" },
    ],
  },

  // ── 내 업무 공간(my) — B′ 미리보기. 로그인만으로 보임(gate null), 권한 편집기에서 숨김 ──
  {
    key: "my", label: "홈", icon: "home", group: "",
    spaces: ["my"], gate: null, work: [], hideInRoleEditor: true,
  },
  {
    // 일정·할 일 — 월 캘린더 + 개인 일정·할 일(users/{uid}/personal_events) + 법인 생산·휴일 겹쳐 보기(2026-10-19 2차)
    key: "my/tasks", label: "일정·할 일", icon: "calendar", group: "내 일",
    spaces: ["my"], gate: null, work: [], hideInRoleEditor: true,
  },
  {
    key: "my/leave", label: "휴가·지출", icon: "receipt", group: "내 일",
    spaces: ["my"], gate: null, work: [], comingSoon: true, marker: "준비중", hideInRoleEditor: true,
  },
  {
    key: "my/pay", label: "내 급여", icon: "card", group: "내 일",
    spaces: ["my"], gate: null, work: [], comingSoon: true, marker: "준비중", hideInRoleEditor: true,
  },
  {
    // 급여 관리(통합) — 회계 담당자가 두 법인 급여를 한 화면에서(D-27, 2026-10-22). 화면은 급여관리 그대로
    // (명부·명세에 법인 구분이 있어 이미 두 법인을 함께 다룸). 권한은 급여 등록(payroll.import) — 편집기는 급여관리 행에서 관리.
    key: "my/payroll", label: "급여 관리", icon: "card", group: "담당 업무",
    spaces: ["my"], gate: ["payroll.import"], work: [], hideInRoleEditor: true,
  },
  {
    // 그룹 "" → 사이드바에서 구분선 뒤에 놓인다(navFor 참고)
    key: "my/all", label: "전체 보기", icon: "dashboard", group: "",
    spaces: ["my"], gate: null, work: [], hideInRoleEditor: true,
  },
];

/**
 * 알림 "수신 자격" 권한 — 메뉴가 아니라 그룹별 알림설정 전용 모달(RoleNotifyModal)에서 다룬다.
 * 저장은 일반 권한과 동일(roles.permissions 배열). 백엔드 collectRecipientTokens 가 평가.
 * 도메인이 늘면 여기 한 줄 추가(예: notify.sales) + push_copy.PUSH_TYPE_PERM + 백엔드 매핑.
 */
export const NOTIFY_PERMS: WorkPerm[] = [
  { key: "notify.shipment", label: "출하 알림 수신" },
];

/**
 * 메뉴가 아닌 공통 기능 권한 — 화면(사이드바 메뉴) 없이 여러 곳에서 쓰는 자료 묶음.
 * 권한 편집기 "공통" 묶음에 "공통 기능" 항목으로 나온다(gate 없음, 업무권한 토글만).
 * 메뉴 게이트가 아니므로 '전체 메뉴 허용'으로 켜지지 않는다.
 */
export const COMMON_FEATURE_PERMS: WorkPerm[] = [
  // 두 법인 날짜별 출하 예정(현장·물량 요약, 배합 코드 없음). 화면은 아직 없음(§6-4 구내식당).
  { key: "calendar.view", label: "출하 일정 보기" },
  // 거래처·현장 이름·코드·주소·담당자 검색(미수·여신·매출 없음). 검색용 목록 분리는 §5-A 에서.
  { key: "directory.view", label: "거래처·현장 검색" },
];

/**
 * 옛 키 → 새 키 별칭. 기존 역할 문서에 남아 있는 옛 키를 새 키와 같은 뜻으로 판정한다.
 * 편집기에서는 옛 키를 별도 토글로 보이지 않는다(개별 권한에 옛 키 오버라이드가 있으면 그것만 보여 정리할 수 있게).
 *   dispatch.view → shipment.view (출하조회. rules 도 둘을 OR 로 본다)
 *   material.view → material.price (원재료단가 옛 메뉴 키. 홈 위젯·rules 에서 쓰던 것)
 */
export const PERM_LEGACY_ALIASES: Readonly<Record<string, string>> = {
  "dispatch.view": "shipment.view",
  "material.view": "material.price",
};

/** 판정용 키 묶음 — 새 키 + 그 키를 가리키는 옛 별칭들 (anyOf 로 쓴다) */
export function permKeysWithAliases(key: string): string[] {
  const out = [key];
  for (const [legacy, canonical] of Object.entries(PERM_LEGACY_ALIASES)) {
    if (canonical === key) out.push(legacy);
  }
  return out;
}

export function isLegacyAliasKey(key: string): boolean {
  return key in PERM_LEGACY_ALIASES;
}

/** 모든 메뉴 게이트 키 (전체허용 판정·rules 동기화 기준). 전부 `*.view`. */
export const MENU_GATE_KEYS: ReadonlySet<string> = new Set(
  MENU_REGISTRY.flatMap((m) => m.gate ?? []),
);

/** 키 → 한글 라벨 (gate + work 전체 + 알림 권한) */
export const PERMISSION_LABEL: ReadonlyMap<string, string> = new Map<string, string>([
  // hideInRoleEditor 메뉴(공간 미리보기 자리 등)는 기존 게이트를 빌려 쓰므로 라벨을 덮어쓰지 않게 제외
  ...MENU_REGISTRY.filter((m) => !m.hideInRoleEditor).flatMap((m) => [
    ...(m.gate ?? []).map((k) => [k, `${m.label} 조회`] as const),
    ...m.work.map((w) => [w.key, w.label] as const),
  ]),
  ...COMMON_FEATURE_PERMS.map((w) => [w.key, w.label] as const),
  ...NOTIFY_PERMS.map((w) => [w.key, w.label] as const),
  // 옛 별칭은 "(옛 키)" 를 붙여 새 키와 구분 — 개별 권한에 남은 오버라이드 정리용
  ["dispatch.view", "출하조회 조회 (옛 키)"],
  ["material.view", "원자재 단가·금액 보기 (옛 키)"],
]);

/** 전체 권한 키 (평탄, 중복 제거) */
export const ALL_PERMISSION_KEYS: string[] = [...PERMISSION_LABEL.keys()];

/**
 * 권한 편집기 한 항목 — 메뉴 하나 또는 메뉴 없는 기능 묶음 하나.
 * RoleEditModal·UserPermsModal 이 이 목록(ROLE_EDITOR_SECTIONS)으로 좌측 목록을 그린다.
 */
export interface PermSection {
  key: string;
  label: string;
  group: PermGroup;
  /** 메뉴권한(anyOf, 옛 별칭 포함). null = 게이트 없음(항상 표시 메뉴 또는 기능 묶음) */
  gate: string[] | null;
  work: WorkPerm[];
  comingSoon?: boolean;
  /** false = 사이드바 메뉴가 아닌 기능 묶음 */
  isMenu: boolean;
  /** 메뉴가 놓이는 법인 공간(MenuDef.spaces, 생략 = 중앙만) */
  spaces?: SpaceKey[];
  /** "tenant" = 법인 공통 메뉴(급여·원가·환경설정) */
  scope?: "tenant" | "biz";
}

/** 편집기 좌측 목록 — PERM_GROUPS 순서, 묶음 안에서는 MENU_REGISTRY 순서. 공통 기능은 공통 묶음 끝. */
export const ROLE_EDITOR_SECTIONS: PermSection[] = (() => {
  const items: PermSection[] = [
    ...MENU_REGISTRY.filter((m) => !m.hideInRoleEditor).map((m) => ({
      key: m.key, label: m.label, group: m.permGroup ?? "공통", gate: m.gate, work: m.work,
      comingSoon: m.comingSoon, isMenu: true, spaces: m.spaces, scope: m.scope,
    })),
    { key: "common-features", label: "공통 기능", group: "공통", gate: null, work: COMMON_FEATURE_PERMS, isMenu: false },
  ];
  return PERM_GROUPS.flatMap((g) => items.filter((it) => it.group === g));
})();

/**
 * 사업부 부서 편집기 항목(12 §16-2) — 그 법인 공간에 있는 메뉴만. 법인 공통 메뉴(scope "tenant")는 권한 기준이 되는
 * 대표 사업부(defaultBiz)에서만 보인다(다른 사업부 부서 문서의 같은 키는 판정에 쓰이지 않음). 메뉴 아닌 기능 묶음은 항상.
 */
export function roleEditorSectionsFor(tenant: TenantKey, biz: BizKey): PermSection[] {
  const main = biz === defaultBiz(tenant);
  return ROLE_EDITOR_SECTIONS.filter(
    (s) => !s.isMenu || ((s.spaces ?? ["joongang"]).includes(tenant) && (s.scope !== "tenant" || main)),
  );
}

/** 편집기에 토글로 보일 게이트 키 (옛 별칭 제외) */
export function editorGateKeys(sec: Pick<PermSection, "gate">): string[] {
  return (sec.gate ?? []).filter((k) => !isLegacyAliasKey(k));
}

export interface PermCatalogGroup {
  label: string;
  perms: { key: string; label: string }[];
}

/**
 * 권한 카탈로그 — 편집기 묶음(PERM_GROUPS)별 (중복 제거된) 권한 키 목록. 옛 별칭 키는 빠진다.
 * gate(.view)는 "{메뉴} 조회"로 표기.
 */
export const PERMISSION_CATALOG: PermCatalogGroup[] = (() => {
  const seen = new Set<string>();
  const byGroup = new Map<string, { key: string; label: string }[]>();
  for (const sec of ROLE_EDITOR_SECTIONS) {
    if (!byGroup.has(sec.group)) byGroup.set(sec.group, []);
    const arr = byGroup.get(sec.group)!;
    for (const k of editorGateKeys(sec)) {
      if (!seen.has(k)) {
        seen.add(k);
        arr.push({ key: k, label: `${sec.label} 조회` });
      }
    }
    for (const w of sec.work) {
      if (!seen.has(w.key)) {
        seen.add(w.key);
        arr.push({ key: w.key, label: w.label });
      }
    }
  }
  return PERM_GROUPS.map((g) => ({ label: g as string, perms: byGroup.get(g) ?? [] })).filter((grp) => grp.perms.length > 0);
})();

// ───────────────────────── 사이드바(nav) 파생 ─────────────────────────

export type NavItem = {
  type: "item";
  icon: IconName;
  label: string;
  key: string;
  /** 진입 게이트 (anyOf). null 이면 항상 표시. */
  gate: string[] | null;
  marker?: string;
  comingSoon?: boolean;
};
/** label "" = 이름 없는 구분선(앞 그룹과 끊어 보이게) */
export type NavGroup = { type: "group"; label: string };
export type NavEntry = NavItem | NavGroup;

/** 법인 공통 메뉴인가(D-26) — 사업 단계 없이 `/c/{tenant}/{key}` */
export function isTenantScopeMenu(key: string): boolean {
  return MENU_REGISTRY.some((m) => m.key === key && m.scope === "tenant");
}

/**
 * 메뉴 key → URL.
 *   내 업무 메뉴("my", "my/…") → `/my…` (공간 인자 무시)
 *   법인 공통 메뉴(scope "tenant") → `/c/{tenant}/{key}`
 *   사업 메뉴 → `/c/{tenant}/{biz}/{key}`, 사업 홈(dashboard) → `/c/{tenant}/{biz}`
 *   biz 생략 또는 그 법인에 없는 사업이면 그 법인의 기본 사업.
 * space 가 "my" 인데 법인 메뉴를 가리키면 옛 주소와 같은 법인(LEGACY_TENANT)으로 만든다 —
 * 호출부는 가능하면 대상 법인을 명시한다(전체 보기·검색·알림 링크).
 */
export function menuPath(key: string, space: SpaceKey, biz?: BizKey | null): string {
  if (key === "my" || key.startsWith("my/")) return `/${key}`;
  const tenant = isTenantKey(space) ? space : LEGACY_TENANT;
  if (isTenantScopeMenu(key)) return `${tenantRootPath(tenant)}/${key}`;
  const home = bizHomePath(tenant, resolveMenuBiz(tenant, key, biz));
  return key === "dashboard" ? home : `${home}/${key}`;
}

/** 메뉴가 놓이는 사업 목록(생략 = LEGACY_BIZ) */
export function menuBizList(m: MenuDef): readonly BizKey[] {
  return m.biz ?? LEGACY_BIZ;
}

/** 이 사업 사이드바에 key 메뉴가 있나 (공간·권한 무관). 등록 안 된 key(별칭 경로 등)는 false */
export function bizHasMenu(tenant: TenantKey, biz: BizKey, key: string): boolean {
  const m = findMenu(key);
  return !!m && menuSpaces(m).includes(tenant) && menuBizList(m).includes(biz);
}

/** 그 사업 사이드바의 첫 메뉴 key — 홈(대시보드)이 없는 사업(석산)의 사업 홈으로 쓴다 */
export function firstBizMenuKey(tenant: TenantKey, biz: BizKey): string | undefined {
  return MENU_REGISTRY.find((m) => menuSpaces(m).includes(tenant) && m.scope !== "tenant" && menuBizList(m).includes(biz))?.key;
}

/**
 * key 메뉴 주소에 쓸 사업 — 요청한(또는 기본) 사업에 그 메뉴가 있으면 그대로, 없으면 그 법인에서 메뉴가 있는 첫 사업.
 * (석산 전용 메뉴를 사업 없이 부르면 레미콘이 아니라 석산 주소가 나오게)
 */
function resolveMenuBiz(tenant: TenantKey, key: string, biz?: BizKey | null): BizKey {
  const want = tenantHasBiz(tenant, biz) ? biz : defaultBiz(tenant);
  const m = findMenu(key);
  if (!m) return want;
  const list = menuBizList(m);
  if (list.includes(want)) return want;
  return TENANT_BIZ[tenant].find((b) => list.includes(b)) ?? want;
}

/**
 * URL → 메뉴 key (menuPath 의 역). 내 업무는 두 단계("my/tasks"), 법인은 /c/{tenant}[/{biz}] 뒤 첫 세그먼트,
 * 법인 구분 없는 옛 주소는 첫 세그먼트.
 */
export function menuKeyOfPath(pathname: string): string {
  const p = parseSpacePath(pathname);
  const segs = p.rest.split("/").filter(Boolean);
  if (p.space === "my") return segs.length > 0 ? `my/${segs[0]}` : "my";
  if (segs.length === 0) return "dashboard";
  return segs[0];
}

/**
 * 옛 주소(법인 구분 없음) 첫 세그먼트 → 리다이렉트 대상. 라우터가 `/{key}/*` 를 menuPath(key, 중앙) 아래로 보낸다
 * (사업 메뉴 `/c/joongang/rmc/{key}…`, 법인 공통 `/c/joongang/{key}…`).
 * 법인 메뉴 전부 + 옛 별칭(dashboard·material·route). 새 법인 메뉴를 추가하면 자동 포함.
 */
export const LEGACY_PATH_KEYS: readonly string[] = [
  ...new Set([
    ...MENU_REGISTRY.filter((m) => !(m.spaces ?? []).includes("my")).map((m) => m.key),
    "dashboard", "material", "route",
  ]),
];

/** 공간 홈 메뉴인가 (NavLink end 매칭·전환 안내 생략 판단) */
export function isHomeKey(key: string): boolean {
  return key === "dashboard" || key === "my";
}

function menuSpaces(m: MenuDef): SpaceKey[] {
  return m.spaces ?? ["joongang"];
}

/**
 * 공간별 사이드바 목록 — MENU_REGISTRY 순서대로, 그 공간·사업 메뉴만 골라 그룹 헤더를 끼운다.
 * 법인 공간이면 사업(biz 생략·없는 사업 = 기본 사업)으로 한 번 더 거른다(2026-10-29 — 석산에 레미콘 메뉴가 안 보이게).
 * 권한 필터는 여기서 하지 않는다(호출부가 기존 gate·hasPerm 그대로 적용).
 */
export function navFor(space: SpaceKey, biz?: BizKey | null): NavEntry[] {
  const out: NavEntry[] = [];
  let lastGroup: string | null = null;
  const b: BizKey | null = isTenantKey(space) ? (tenantHasBiz(space, biz) ? biz : defaultBiz(space)) : null;
  for (const m of MENU_REGISTRY) {
    if (!menuSpaces(m).includes(space)) continue;
    if (b && !menuBizList(m).includes(b)) continue;
    if (m.group !== lastGroup) {
      if (m.group) out.push({ type: "group", label: m.group });
      else if (lastGroup) out.push({ type: "group", label: "" }); // 그룹 → 무그룹 복귀 = 구분선
    }
    lastGroup = m.group;
    out.push({
      type: "item", icon: m.icon, label: m.label, key: m.key, gate: m.gate,
      marker: m.marker, comingSoon: m.comingSoon,
    });
  }
  return out;
}

/** 이 공간 사이드바에 key 메뉴가 있나 (권한 무관) */
export function spaceHasMenu(space: SpaceKey, key: string): boolean {
  return MENU_REGISTRY.some((m) => m.key === key && menuSpaces(m).includes(space));
}

export function findMenu(key: string): MenuDef | undefined {
  return MENU_REGISTRY.find((m) => m.key === key);
}

/** 옛 평탄 NAV — 현행(중앙산업) 메뉴 그대로. 호환용. */
export const NAV: NavEntry[] = navFor("joongang");
